import type { InternalAxiosRequestConfig } from "axios";

// Refresh when the token has less than this left: a request sent with a
// token that dies in flight (latency + transient retry) still 401s.
const DEFAULT_MARGIN_MS = 15_000;

// Only the Cognito JWT flow is refreshable. Opaque bearers (`jwt-as:`, `pat:`)
// are owned by the platform and must never be swapped for a Cognito JWT.
const JWT_BEARER_PREFIX = "Bearer jwt:";

// One entry per bearer ever authorized; the set stays tiny (one token per
// hour) but is bounded anyway so a pathological caller cannot grow it.
const MAX_CACHE_ENTRIES = 16;

export type TokenGateOptions = {
  // Re-establishes the session and returns the new bearer, or null when no
  // session could be obtained (the request is then sent as-is and the 401
  // path takes over). Concurrent requests share a single in-flight call.
  refresh: () => Promise<string | null>;
  marginMs?: number;
  now?: () => number;
};

export type TokenGate = {
  request: (
    config: InternalAxiosRequestConfig,
  ) => Promise<InternalAxiosRequestConfig>;
  // Pre-computes the expiry for a bearer so the request path is a cache hit.
  // Meant to be called wherever the default Authorization header is set.
  remember: (bearer: string) => void;
  // Expiry in ms since epoch, or null for opaque / undecodable bearers.
  getExpiry: (bearer: string) => number | null;
};

/**
 * Request interceptor that refreshes an expired (or about to expire) Cognito
 * JWT *before* the request leaves, instead of letting it 401 and replaying.
 *
 * Why: a backgrounded tab has its expiry timer throttled, so on refocus every
 * stale query fires at once with a dead token. Each one 401s, refreshes,
 * replays — and the first replay can still race the session repair. Gating
 * on the client-side `exp` claim turns that into a single awaited refresh
 * that all pending requests wait on.
 */
export function createTokenGate({
  refresh,
  marginMs = DEFAULT_MARGIN_MS,
  now = () => Date.now(),
}: TokenGateOptions): TokenGate {
  const expiries = new Map<string, number | null>();
  let inFlight: Promise<string | null> | null = null;

  const remember = (bearer: string): number | null => {
    const cached = expiries.get(bearer);
    if (cached !== undefined) {
      return cached;
    }
    const exp = decodeExpiry(bearer);
    if (expiries.size >= MAX_CACHE_ENTRIES) {
      const oldest = expiries.keys().next().value;
      if (oldest !== undefined) {
        expiries.delete(oldest);
      }
    }
    expiries.set(bearer, exp);
    return exp;
  };

  const sharedRefresh = (): Promise<string | null> => {
    if (!inFlight) {
      inFlight = (async () => {
        try {
          return await refresh();
        } catch (error) {
          // Transient failure: let the request go with the old token and
          // leave the verdict to the response-side 401 handling.
          // oxlint-disable-next-line no-console
          console.warn("token-gate: refresh failed, sending as-is", error);
          return null;
        } finally {
          inFlight = null;
        }
      })();
    }
    return inFlight;
  };

  const request = async (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    // A replayed request already carries a freshly refreshed token; gating
    // it again could only loop.
    if (config.doNotAuthRetry) {
      return config;
    }
    const bearer = config.headers.Authorization;
    if (typeof bearer !== "string" || !bearer.startsWith(JWT_BEARER_PREFIX)) {
      return config;
    }
    const exp = remember(bearer);
    if (exp === null || exp - now() > marginMs) {
      return config;
    }
    const fresh = await sharedRefresh();
    if (fresh && fresh !== bearer) {
      remember(fresh);
      config.headers.Authorization = fresh;
    }
    return config;
  };

  return {
    request,
    remember: (bearer) => {
      remember(bearer);
    },
    getExpiry: remember,
  };
}

// Reads the `exp` claim (seconds) off the JWT payload; returns milliseconds,
// or null when the bearer is not a decodable JWT with a numeric `exp`.
function decodeExpiry(bearer: string): number | null {
  const token = bearer.slice(JWT_BEARER_PREFIX.length);
  const payload = token.split(".")[1];
  if (!payload) {
    return null;
  }
  try {
    // JWT segments are base64url without padding. Per spec `atob` accepts
    // unpadded input, but padding it costs nothing and removes any doubt on
    // older engines.
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const json: unknown = JSON.parse(globalThis.atob(padded));
    if (
      typeof json === "object" &&
      json !== null &&
      "exp" in json &&
      typeof json.exp === "number"
    ) {
      return json.exp * 1000;
    }
    return null;
  } catch {
    return null;
  }
}
